import { useCallback, useEffect, useRef, useState } from 'react';

import type { DealerSearch } from '@/app/components/shared/dealer/utils/types';
import { cn } from '@/app/utils/cn';
import { DealerGrouping } from '@/services/contentstack/models/dealer';

interface InfiniteScrollListProps {
  className?: string;
  items: DealerSearch[] | DealerGrouping[];
  renderItem: (
    item: DealerSearch | DealerGrouping,
    index: number
  ) => React.ReactNode;
  itemsPerLoad?: number;
}

export const InfiniteScrollList = ({
  className,
  items = [],
  renderItem,
  itemsPerLoad = 10,
}: InfiniteScrollListProps) => {
  const [visibleCount, setVisibleCount] = useState<number>(itemsPerLoad);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const resultItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  const loadMore = useCallback(() => {
    if (!hasMore) return;

    setVisibleCount((prev) => Math.min(prev + itemsPerLoad, items.length));
  }, [hasMore, items.length, itemsPerLoad]);

  const lastItemRef = useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            loadMore();
          }
        },
        {
          threshold: 0.1,
          rootMargin: '50px',
        }
      );

      if (node) observerRef.current.observe(node);
    },
    [hasMore, loadMore]
  );

  useEffect(() => {
    setVisibleCount(itemsPerLoad);
  }, [items, itemsPerLoad]);

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return resultItems.map(
    (item: DealerSearch | DealerGrouping, index: number) => (
      <div
        className={cn(className, {
          'pb-4': index === resultItems.length - 1,
        })}
        key={`${index}-${visibleCount}`}
        ref={index === resultItems.length - 1 ? lastItemRef : null}
        style={{
          containIntrinsicSize: 'auto 100px',
          contentVisibility: 'auto',
        }}
      >
        {renderItem(item, index)}
      </div>
    )
  );
};
